import { first } from 'rxjs/operators'
import { defineComponent } from 'vue'

import { Item, Movement, getTranslatedString, LinkableItemLifecycleStates, Location, SearchQuery } from '@skinsoft/types'
import { itemSelectors, locationSelectors, movementSelectors } from '@skinsoft/selectors'
import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { StateInput, ValidationStates } from '@skinsoft/ui'
import { LocationSelectorPopup } from '@skinsoft/entity-ui/src/location/location-selector-popup'

export const LocationInput = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sLocationSelectorPopup: LocationSelectorPopup,
        sStateInput: StateInput,
    },
    props: ['item', 'movement', 'placeholder'],
    template: require('./location-input.html'),
    data() {
        return {
            ValidationStates,
            itemSelectors,
            selectedLocation: null as any as Location,
            permanentLocationHint: "",
            locations: null as Location[] | null,
        }
    },
    computed: {
        realTimeLocation(): ReturnType<typeof itemSelectors.realTimeLocation> {
            return itemSelectors.realTimeLocation(this.item);
        },
        linkableItemState(): ReturnType<typeof movementSelectors.linkableItemState> | null {
            return this.movement ? movementSelectors.linkableItemState(this.movement, this.item.id) : null;
        },
    },
    mounted() {
        this.subscribeToSyncQueue(this.syncLinkableItemState.bind(this));
        this.triggerSync();
    },
    methods: {
        async validator(value: string) {
            if (value) {
                try {
                    const searchQuery: SearchQuery = {
                        queryText: value,
                    }

                    this.selectedLocation = (await (this as any).$locationDataStore.searchAll(searchQuery).pipe(first()).toPromise()).allData[0];
                    if (!this.selectedLocation) {
                        this.selectedLocation = {id: value, translatedTitle: {translatedString: {fr: ""}}}
                    }
                    return true;
                } catch (error) {
                    return false;
                }
            }
        },
        syncLinkableItemState() {
            if (!this.$refs.stateInput) {
                // for some reason it is undefined for a while after mount
                return;
            }

            if (!this.movement) {
                switch (this.linkableItemState) {
                    case LinkableItemLifecycleStates.FINALIZED:
                        (this.$refs.stateInput as InstanceType<typeof StateInput>).validate(this.realTimeLocation);
                        break;
                    case LinkableItemLifecycleStates.SELECTED:
                    case LinkableItemLifecycleStates.IN_TRANSIT:
                        (this.$refs.stateInput as InstanceType<typeof StateInput>).reset();
                        break;
                }
            }
        },
        openPopup() {
            (this.$refs.locationPopup as InstanceType<typeof LocationSelectorPopup>).open()
        },
        onLocationValidated(locationValidatedEvent) {
            let locationTitle = locationSelectors.title(locationValidatedEvent.location, this.getAppLocale())
            // the translatedTitle is included in the title selector, but I'm still wondering if we should let
            //translatedTitle inside title selector, so I let the line bellow
            if(!locationTitle) {
                locationSelectors.translatedTitle(locationValidatedEvent.location, this.getAppLocale())
            }

            (this.$refs.stateInput as InstanceType<typeof StateInput>).setValue(locationTitle)
            this.selectedLocation = locationValidatedEvent.location;
            this.$emit('change', this.selectedLocation)
        },
        subscribeToLocationHint() {
            // Retrieving permanent location as a hint to display
            const permanentLocationId = itemSelectors.permanentLocation(this.item);
            if (permanentLocationId) {
                this.subscribeTo(
                    (this as any).$locationDataStore.fetchOne(
                        permanentLocationId
                    ).pipe(first()),
                    async (location) => {
                        const pathIds: string | undefined | null = locationSelectors.pathIds(location)
                        if(pathIds) {
                            this.permanentLocationHint = await (this as any).$locationDataStore.resolvePath(pathIds, this.getAppLocale())
                        } else {
                            this.permanentLocationHint = ""
                        }
                    },
                    'permanent-location-hint-subscription')
            }
        },
        unsubscribeFromLocationHint() {
            this.unsubscribeFrom('permanent-location-hint-subscription')
        },
    },
    watch: {
        linkableItemState: {
            handler() {
                this.triggerSync();
            },
            immediate: false,
            deep: true,
        },
        item: {
            handler() {
                this.unsubscribeFromLocationHint();
                this.subscribeToLocationHint();
            },
            immediate: true,
        },
    },
})
